import { useEffect, useState } from "react";
import "./YoutubeVideos.css";

import API_BASE_URL from "../config/config";

function YoutubeVideos() {

    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [currentSlide, setCurrentSlide] = useState(0);

    const API_URL = API_BASE_URL.replace(/\/$/, "");

    /*
    |--------------------------------------------------------------------------
    | Fetch Videos
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        const fetchVideos = async () => {

            try {

                setLoading(true);
                setError("");

                const response = await fetch(
                    `${API_URL}/user-youtube`,
                    {
                        method: "GET",
                        headers: {
                            Accept: "application/json"
                        }
                    }
                );

                const text = await response.text();

                console.log(
                    "YouTube API URL:",
                    `${API_URL}/youtube-videos`
                );

                console.log(
                    "YouTube API Status:",
                    response.status
                );

                console.log(
                    "YouTube API Response:",
                    text
                );

                let data;

                try {

                    data = JSON.parse(text);

                } catch (jsonError) {

                    throw new Error(
                        `Server returned invalid JSON. HTTP Status: ${response.status}`
                    );
                }

                if (!response.ok) {

                    throw new Error(
                        data.message ||
                        `Failed to fetch YouTube videos (${response.status})`
                    );
                }

                if (Array.isArray(data.videos)) {

                    setVideos(data.videos);

                } else if (Array.isArray(data.data)) {

                    setVideos(data.data);

                } else if (Array.isArray(data)) {

                    setVideos(data);

                } else {

                    setVideos([]);

                    setError(
                        data.message ||
                        "No YouTube videos available."
                    );
                }

            } catch (error) {

                console.error(
                    "YouTube Videos API Error:",
                    error
                );

                setError(
                    error.message ||
                    "Unable to load YouTube videos."
                );

                setVideos([]);

            } finally {

                setLoading(false);
            }
        };

        fetchVideos();

    }, [API_URL]);


    /*
    |--------------------------------------------------------------------------
    | Auto Slider
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        if (videos.length <= 1) {
            return;
        }

        const interval = setInterval(() => {

            setCurrentSlide((prev) => {

                if (prev >= videos.length - 1) {
                    return 0;
                }

                return prev + 1;
            });

        }, 6000);

        return () => {
            clearInterval(interval);
        };

    }, [videos.length]);


    /*
    |--------------------------------------------------------------------------
    | Previous
    |--------------------------------------------------------------------------
    */

    const handlePrevious = () => {

        setCurrentSlide((prev) => {

            if (prev <= 0) {
                return videos.length - 1;
            }

            return prev - 1;
        });
    };


    /*
    |--------------------------------------------------------------------------
    | Next
    |--------------------------------------------------------------------------
    */

    const handleNext = () => {

        setCurrentSlide((prev) => {

            if (prev >= videos.length - 1) {
                return 0;
            }

            return prev + 1;
        });
    };


    /*
    |--------------------------------------------------------------------------
    | Dot
    |--------------------------------------------------------------------------
    */

    const handleDotClick = (index) => {

        setCurrentSlide(index);
    };


    /*
    |--------------------------------------------------------------------------
    | Convert YouTube URL To Embed URL
    |--------------------------------------------------------------------------
    */

    const getYoutubeEmbedUrl = (url) => {

        if (!url) {
            return "";
        }

        try {

            /*
             * Already embed URL
             */
            if (url.includes("youtube.com/embed/")) {

                return url;
            }


            /*
             * youtube.com/watch?v=VIDEO_ID
             */
            if (url.includes("youtube.com/watch")) {

                const urlObject = new URL(url);

                const videoId =
                    urlObject.searchParams.get("v");

                if (videoId) {

                    return `https://www.youtube.com/embed/${videoId}`;
                }
            }


            /*
             * youtu.be/VIDEO_ID
             */
            if (url.includes("youtu.be/")) {

                const videoId =
                    url.split("youtu.be/")[1]
                        ?.split("?")[0]
                        ?.split("&")[0];

                if (videoId) {

                    return `https://www.youtube.com/embed/${videoId}`;
                }
            }


            /*
             * youtube.com/shorts/VIDEO_ID
             */
            if (url.includes("youtube.com/shorts/")) {

                const videoId =
                    url.split("youtube.com/shorts/")[1]
                        ?.split("?")[0]
                        ?.split("&")[0];

                if (videoId) {

                    return `https://www.youtube.com/embed/${videoId}`;
                }
            }


            /*
             * If already looks like an ID
             */
            if (
                !url.includes("/") &&
                !url.includes(":")
            ) {

                return `https://www.youtube.com/embed/${url}`;
            }

        } catch (error) {

            console.error(
                "Invalid YouTube URL:",
                error
            );
        }

        return url;
    };


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (
            <section className="youtube-videos-section">

                <div className="youtube-videos-container">

                    <div className="youtube-section-heading">

                        <span className="youtube-badge">
                            YOUTUBE VIDEOS
                        </span>

                        <h2>
                            Learn From Our{" "}
                            <strong>Videos</strong>
                        </h2>

                        <p>
                            Watch useful videos and learning
                            content from HomeoAdda.
                        </p>

                    </div>

                    <div className="youtube-loading">

                        <div className="youtube-spinner"></div>

                        <span>
                            Loading videos...
                        </span>

                    </div>

                </div>

            </section>
        );
    }


    /*
    |--------------------------------------------------------------------------
    | Error
    |--------------------------------------------------------------------------
    */

    if (error) {

        return (
            <section className="youtube-videos-section">

                <div className="youtube-videos-container">

                    <div className="youtube-section-heading">

                        <span className="youtube-badge">
                            YOUTUBE VIDEOS
                        </span>

                        <h2>
                            Learn From Our{" "}
                            <strong>Videos</strong>
                        </h2>

                    </div>

                    <div className="youtube-error">
                        {error}
                    </div>

                </div>

            </section>
        );
    }


    /*
    |--------------------------------------------------------------------------
    | No Videos
    |--------------------------------------------------------------------------
    */

    if (videos.length === 0) {
        return null;
    }


    return (

        <section className="youtube-videos-section">

            <div className="youtube-videos-container">


                {/* =====================================================
                    SECTION HEADING
                ===================================================== */}

                <div className="youtube-section-heading">

                    <span className="youtube-badge">
                        YOUTUBE VIDEOS
                    </span>

                    <h2>
                        Learn From Our{" "}
                        <strong>Videos</strong>
                    </h2>

                    <p>
                        Watch useful videos and learning
                        content from HomeoAdda.
                    </p>

                </div>


                {/* =====================================================
                    SLIDER
                ===================================================== */}

                <div className="youtube-slider-wrapper">


                    {/* Previous */}

                    {videos.length > 1 && (

                        <button
                            type="button"
                            className="youtube-slider-btn youtube-prev"
                            onClick={handlePrevious}
                            aria-label="Previous video"
                        >
                            &#10094;
                        </button>

                    )}


                    {/* Slider */}

                    <div className="youtube-slider">

                        <div
                            className="youtube-track"
                            style={{
                                transform:
                                    `translateX(-${currentSlide * 100}%)`
                            }}
                        >

                            {videos.map((video, index) => (

                                <div
                                    className="youtube-slide"
                                    key={
                                        video.id ||
                                        index
                                    }
                                >

                                    <div className="youtube-card">


                                        {/* =================================================
                                            LEFT CONTENT
                                        ================================================= */}

                                        <div className="youtube-content">

                                            <span className="youtube-content-label">
                                                HOMEoADDA VIDEO
                                            </span>

                                            <h3>
                                                {video.title}
                                            </h3>

                                            {video.author && (

                                                <div className="youtube-author">

                                                    <span className="youtube-author-icon">
                                                        👨‍⚕️
                                                    </span>

                                                    <span>
                                                        {video.author}
                                                    </span>

                                                </div>

                                            )}


                                            <div
                                                className="youtube-description"
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        video.description ||
                                                        ""
                                                }}
                                            />


                                            <div className="youtube-content-line"></div>


                                            <div className="youtube-video-number">

                                                Video{" "}
                                                {index + 1}
                                                {" "}
                                                of{" "}
                                                {videos.length}

                                            </div>

                                        </div>


                                        {/* =================================================
                                            RIGHT VIDEO
                                        ================================================= */}

                                        <div className="youtube-video-wrapper">

                                            <div className="youtube-video-frame">

                                                {video.video_link ? (

                                                    <iframe
                                                        src={getYoutubeEmbedUrl(
                                                            video.video_link
                                                        )}
                                                        title={
                                                            video.title ||
                                                            "YouTube Video"
                                                        }
                                                        loading="lazy"
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                        allowFullScreen
                                                    ></iframe>

                                                ) : (

                                                    <div className="youtube-no-video">
                                                        Video not available
                                                    </div>

                                                )}

                                            </div>

                                        </div>

                                    </div>

                                </div>

                            ))}

                        </div>

                    </div>


                    {/* Next */}

                    {videos.length > 1 && (

                        <button
                            type="button"
                            className="youtube-slider-btn youtube-next"
                            onClick={handleNext}
                            aria-label="Next video"
                        >
                            &#10095;
                        </button>

                    )}

                </div>


                {/* =====================================================
                    DOTS
                ===================================================== */}

                {videos.length > 1 && (

                    <div className="youtube-dots">

                        {videos.map((video, index) => (

                            <button
                                type="button"
                                key={
                                    video.id ||
                                    index
                                }
                                className={
                                    `youtube-dot ${
                                        currentSlide === index
                                            ? "active"
                                            : ""
                                    }`
                                }
                                onClick={() =>
                                    handleDotClick(index)
                                }
                                aria-label={
                                    `Go to video ${index + 1}`
                                }
                            ></button>

                        ))}

                    </div>

                )}

            </div>

        </section>
    );
}

export default YoutubeVideos;